import type { HttpError } from '@refinedev/core'
import { useList, useNotification } from '@refinedev/core'
import type { PlatformUser, Tenant, TenantRole } from '@sirius/contracts/admin'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useAdminClient } from '~/api/adminClientContext'
import { ConfirmDialog } from '~/components/confirmDialog'
import { Button } from '~/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { errorMessage } from '~/providers/errors'
import { TENANT_ROLE_OPTIONS, tenantsToJoin } from './userActions'
import { tenantRoleLabel } from './userFormat'
import { useUserRefresh } from './useUserRefresh'

type MembershipChange = { tenantId: string; role: TenantRole | null }

function RoleSelect({
  value,
  label,
  disabled,
  onChange,
}: {
  value: string
  label: string
  disabled: boolean
  onChange: (role: TenantRole) => void
}) {
  const { t } = useTranslation()
  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(role) => onChange(role as TenantRole)}
    >
      <SelectTrigger
        aria-label={label}
        size="sm"
        className="w-32"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {TENANT_ROLE_OPTIONS.map((role) => (
          <SelectItem
            key={role}
            value={role}
          >
            {tenantRoleLabel(role, t)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/**
 * The user's memberships, as staff edit them: the role in each tenant
 * (`owner` too, which only staff set), Remove behind a confirmation (also a
 * tenant's last owner), and a row to join another tenant.
 */
export function MembershipsEditor({ user }: { user: PlatformUser }) {
  const { t } = useTranslation()
  const client = useAdminClient()
  const refresh = useUserRefresh()
  const { open } = useNotification()
  const tenants = useList<Tenant, HttpError>({
    resource: 'tenants',
    pagination: { mode: 'off' },
    sorters: [{ field: 'name', order: 'asc' }],
  })
  const joinable = tenantsToJoin(tenants.result.data, user.memberships)
  const [tenantToJoin, setTenantToJoin] = useState('')
  const [roleToJoin, setRoleToJoin] = useState<TenantRole>('member')
  const tenantName = (tenantId: string) =>
    user.memberships.find((membership) => membership.tenantId === tenantId)
      ?.tenantName ??
    tenants.result.data.find(({ id }) => id === tenantId)?.name ??
    t('usersPage.memberships.theTenant')

  const change = useMutation({
    mutationFn: ({ tenantId, role }: MembershipChange) =>
      client.users.setMembership({ id: user.id, tenantId, role }),
    onSuccess: async (_user, { tenantId, role }) => {
      await refresh(user.id)
      const tenant = tenantName(tenantId)
      if (!user.memberships.some((joined) => joined.tenantId === tenantId)) {
        setTenantToJoin('')
        setRoleToJoin('member')
      }
      open?.({
        type: 'success',
        message:
          role === null
            ? t('usersPage.memberships.removed', { name: user.name, tenant })
            : t('usersPage.memberships.isRoleOf', {
                name: user.name,
                role: tenantRoleLabel(role, t),
                tenant,
              }),
      })
    },
    onError: (error) => {
      open?.({
        type: 'error',
        message: t('usersPage.memberships.failed'),
        description: errorMessage(error),
      })
    },
  })

  return (
    <section
      aria-labelledby="memberships-heading"
      className="max-w-3xl space-y-2"
    >
      <h3
        id="memberships-heading"
        className="font-semibold"
      >
        {t('usersPage.memberships.heading')}
      </h3>
      {user.memberships.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t('usersPage.memberships.empty')}
        </p>
      ) : (
        <Table
          aria-label={t('usersPage.memberships.heading')}
          className="text-sm"
        >
          <TableHeader>
            <TableRow>
              <TableHead>{t('usersPage.memberships.tenant')}</TableHead>
              <TableHead>{t('usersPage.memberships.slug')}</TableHead>
              <TableHead>{t('usersPage.memberships.role')}</TableHead>
              <TableHead>
                <span className="sr-only">
                  {t('usersPage.memberships.actions')}
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {user.memberships.map(
              ({ tenantId, tenantName: name, tenantSlug, role }) => (
                <TableRow key={tenantId}>
                  <TableCell>
                    <Link
                      className="underline underline-offset-4 hover:text-primary"
                      to={`/platform/tenants/${tenantId}`}
                    >
                      {name}
                    </Link>
                  </TableCell>
                  <TableCell>{tenantSlug}</TableCell>
                  <TableCell>
                    <RoleSelect
                      value={role}
                      label={t('usersPage.memberships.roleIn', {
                        tenant: name,
                      })}
                      disabled={change.isPending}
                      onChange={(next) =>
                        change.mutate({ tenantId, role: next })
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <ConfirmDialog
                      trigger={
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={change.isPending}
                        >
                          {t('usersPage.memberships.remove')}
                        </Button>
                      }
                      title={t('usersPage.memberships.removeTitle', {
                        name: user.name,
                        tenant: name,
                      })}
                      description={t(
                        'usersPage.memberships.removeDescription',
                        { tenant: name },
                      )}
                      confirmLabel={t('usersPage.memberships.remove')}
                      destructive
                      onConfirm={() => change.mutate({ tenantId, role: null })}
                    />
                  </TableCell>
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      )}
      {joinable.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={tenantToJoin}
            disabled={change.isPending}
            onValueChange={setTenantToJoin}
          >
            <SelectTrigger
              aria-label={t('usersPage.memberships.tenantToJoin')}
              size="sm"
              className="w-56"
            >
              <SelectValue
                placeholder={t('usersPage.memberships.addPlaceholder')}
              />
            </SelectTrigger>
            <SelectContent>
              {joinable.map((tenant) => (
                <SelectItem
                  key={tenant.id}
                  value={tenant.id}
                >
                  {tenant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <RoleSelect
            value={roleToJoin}
            label={t('usersPage.memberships.roleInNewTenant')}
            disabled={change.isPending}
            onChange={setRoleToJoin}
          />
          <Button
            size="sm"
            variant="outline"
            disabled={tenantToJoin === '' || change.isPending}
            onClick={() =>
              change.mutate({ tenantId: tenantToJoin, role: roleToJoin })
            }
          >
            {t('usersPage.memberships.add')}
          </Button>
        </div>
      )}
    </section>
  )
}
